import { useState } from 'react';
import type { CoparentSummary, ParentRelationship } from '../../types';
import { useInviteCoparentMutation } from '../../store/api/localApi';
import { useToast } from '../Toast';
import { LinkParentForm, describeLinkOutcome } from '../students/LinkParentForm';

/**
 * "Parents & guardians" on the parent's child home: who else is linked to
 * this child, plus the one action a parent has here — add another parent or
 * guardian by email. Removal is deliberately absent (admin-only).
 *
 * Kept to two fields and one button so a parent needs no explanation: type
 * the other parent's email, say who they are, done. The backend decides
 * whether that means a signup invite or linking an existing account, and the
 * toast reports which. Linking is per child, so the hint after success says
 * how to cover a sibling.
 */
export function CoparentsCard({
  childId,
  childName,
  coparents,
  hasLeft,
}: {
  childId: string;
  childName: string;
  coparents: CoparentSummary[];
  hasLeft: boolean;
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [showSiblingHint, setShowSiblingHint] = useState(false);
  const [inviteCoparent] = useInviteCoparentMutation();
  const { showSuccess, showError } = useToast();

  const handleAdd = async (values: { email: string; relationship: ParentRelationship }) => {
    const { coparent } = await inviteCoparent({ childId, ...values }).unwrap();
    const outcome = describeLinkOutcome(coparent, childName);
    (outcome.tone === 'success' ? showSuccess : showError)(outcome.message);
    setIsAdding(false);
    setShowSiblingHint(outcome.tone === 'success');
  };

  return (
    <section data-testid="parent-child-coparents" className="bg-white rounded-lg shadow">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-900">
          Parents &amp; guardians
          {coparents.length > 0 && (
            <span className="ml-2 text-xs font-normal text-gray-500">· {coparents.length}</span>
          )}
        </h2>
        {!hasLeft && !isAdding && (
          <button
            type="button"
            data-testid="parent-child-coparents-add-btn"
            onClick={() => {
              setShowSiblingHint(false);
              setIsAdding(true);
            }}
            className="shrink-0 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Add a parent
          </button>
        )}
      </div>

      {isAdding && (
        <LinkParentForm
          testIdPrefix="parent-child-coparents-add"
          submitLabel="Add parent"
          onSubmit={handleAdd}
          onCancel={() => setIsAdding(false)}
        />
      )}

      {showSiblingHint && (
        <p
          data-testid="parent-child-coparents-sibling-hint"
          className="border-b border-gray-100 bg-primary-50 px-4 py-2 text-xs text-primary-800"
        >
          To give them access to another child, switch to that child and add the same email.
        </p>
      )}

      {coparents.length === 0 ? (
        <div
          data-testid="parent-child-coparents-empty"
          className="px-4 py-6 text-center text-sm text-gray-500"
        >
          No one else is linked to {childName} yet.
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {coparents.map((cp) => {
            const name = `${cp.firstName} ${cp.lastName}`.trim();
            const isPendingSignup = !name;
            const relationshipLabel = cp.relationship
              ? cp.relationship.charAt(0) + cp.relationship.slice(1).toLowerCase()
              : 'Guardian';
            return (
              <li
                key={cp.id}
                data-testid={`parent-child-coparent-${cp.id}`}
                className="px-4 py-3 flex items-center gap-3"
              >
                <Initials name={name || cp.email} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {name || cp.email}
                    </span>
                    {cp.isPrimary && (
                      <span className="text-[10px] uppercase tracking-wide font-semibold text-primary-700 bg-primary-50 rounded px-1.5 py-0.5">
                        Primary
                      </span>
                    )}
                    {isPendingSignup && (
                      <span
                        data-testid={`parent-child-coparent-pending-${cp.id}`}
                        className="text-[10px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-50 rounded px-1.5 py-0.5"
                      >
                        Invite sent
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {relationshipLabel} · {cp.email}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Initials({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className="h-9 w-9 shrink-0 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold">
      {initials || '?'}
    </div>
  );
}
